import type { ReactNode } from 'react'
import {
  FileText,
  Receipt,
  Building,
  FolderOpen,
  Users,
  UserPlus,
  FilePlus,
  ClipboardList,
  Store,
  Package,
  MessageSquare
} from 'lucide-react'
import { actionHandlers } from '../HomeActions/actionHandlers.ts'
import type { NavigateFunction } from 'react-router-dom'

export const normalizeText = (value: string) =>
  value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')

const levenshtein = (a: string, b: string) => {
  const matrix: number[][] = Array.from({ length: b.length + 1 }, () => [])

  for (let i = 0; i <= b.length; i += 1) {
    matrix[i][0] = i
  }
  for (let j = 0; j <= a.length; j += 1) {
    matrix[0][j] = j
  }

  for (let i = 1; i <= b.length; i += 1) {
    for (let j = 1; j <= a.length; j += 1) {
      if (b.charAt(i - 1) === a.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1]
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] ?? 0, // substitution
          matrix[i][j - 1] ?? 0, // insertion
          matrix[i - 1][j] ?? 0 // deletion
        ) + 1
      }
    }
  }

  return matrix[b.length][a.length]
}

const isFuzzyMatch = (input: string, keyword: string) => {
  if (!input || !keyword) {
    return false
  }
  if (keyword.includes(input) || input.includes(keyword)) {
    return true
  }
  return levenshtein(input, keyword) <= 1
}

export const keywordMatches = (input: string, keywords: string[]) => {
  const normalizedInput = normalizeText(input)
  const tokens = normalizedInput.split(/\s+/).filter(Boolean)

  return keywords.some(keyword => {
    const normalizedKeyword = normalizeText(keyword)
    if (isFuzzyMatch(normalizedInput, normalizedKeyword)) {
      return true
    }
    return tokens.some(token => isFuzzyMatch(token, normalizedKeyword))
  })
}

interface ActionDefinition {
  id: string
  keywords: string[]
  desktop: {
    label: string
    icon: ReactNode
  }
  mobile: {
    label: string
    summary: string
  }
  resolveAction: () => void
}

const buildDefinitions = (
  navigate: NavigateFunction,
  userId?: string,
  userData?: any,
  dispatch?: any
): ActionDefinition[] => [
  {
    id: 'analizar-docs-obra-cotizaciones',
    keywords: [
      'analizar documentacion',
      'documentacion de obra',
      'pliegos obra',
      'especificaciones tecnicas',
      'cotizar con documentacion',
      'revisar documentacion obra',
      'documentacion para cotizar',
      'publicar obra',
      'publicar proyecto',
      'crear obra',
      'subir obra',
      'subir proyecto',
      'necesito presupuestos',
      'recibir propuestas',
      'buscar presupuestos',
      'pedir presupuestos'
    ],
    desktop: {
      label: 'Cotizá tu obra con la documentación que tenés',
      icon: <FileText size={16} />
    },
    mobile: {
      label: '¿Querés cotizar tu obra con la documentación que ya tenés?',
      summary: 'Te orienta en el flujo; en el chat ampliás lo que haga falta sobre tu proyecto.'
    },
    resolveAction: () => actionHandlers['analizar-docs-obra-cotizaciones'](navigate, userId)
  },
  {
    id: 'cotizar-obras',
    keywords: [
      'cotizar',
      'cotizaciones',
      'cotizacion',
      'enviar cotizacion',
      'ofertar',
      'participar en obra',
      'presentar propuesta',
      'cotizar proyecto',
      'cotizar licitacion privada'
    ],
    desktop: {
      label: 'Quiero cotizar en obras privadas disponibles',
      icon: <Receipt size={16} />
    },
    mobile: {
      label: '¿Pensás cotizar en una obra ya publicada?',
      summary: 'Podés ver las oportunidades abiertas, revisar la documentación y enviar tu propuesta en unos pasos usando tu perfil de Edify.'
    },
    resolveAction: () => actionHandlers['cotizar-obras'](navigate, userId)
  },
  {
    id: 'obras-publicas',
    keywords: [
      'obras publicas',
      'licitaciones',
      'licitacion',
      'licitacion publica',
      'estado',
      'gobierno',
      'comprar pliegos',
      'buscar licitacion'
    ],
    desktop: {
      label: 'Explorar licitaciones y obras públicas',
      icon: <Building size={16} />
    },
    mobile: {
      label: '¿Buscás licitaciones o oportunidades del Estado?',
      summary: 'Puedo mostrarte el radar de licitaciones y ayudarte a filtrar por organismo, monto o etapa para evaluar si presentás tu propuesta.'
    },
    resolveAction: () => actionHandlers['obras-publicas'](navigate, userId)
  },
  {
    id: 'mis-proyectos',
    keywords: [
      'mis proyectos',
      'mis obras',
      'gestionar proyectos',
      'proyectos publicados',
      'editar proyecto',
      'administrar obra',
      'actualizar proyecto',
      'gestionar publicaciones'
    ],
    desktop: {
      label: 'Ver y gestionar mis proyectos publicados',
      icon: <FolderOpen size={16} />
    },
    mobile: {
      label: '¿Querés revisar o actualizar tus proyectos publicados?',
      summary: 'Desde acá podés editar información, responder a interesados y mantener al día el estado de cada obra que ya compartiste en Edify.'
    },
    resolveAction: () => actionHandlers['mis-proyectos'](navigate, userData)
  },
  {
    id: 'mis-posteos-muro',
    keywords: [
      'mis posteos',
      'posteos en el muro',
      'muro del perfil',
      'publicaciones perfil',
      'mi muro',
      'ver mi muro',
      'posteos perfil'
    ],
    desktop: {
      label: 'Posteos en el muro de mi perfil',
      icon: <MessageSquare size={16} />
    },
    mobile: {
      label: '¿Querés ver o gestionar tus posteos en el muro?',
      summary: 'Te llevo a la pestaña Muro de tu perfil para revisar lo que publicaste y seguir la conversación con la comunidad.'
    },
    resolveAction: () => actionHandlers['mis-posteos-muro'](navigate, userData)
  },
  {
    id: 'estimacion-costos',
    keywords: [
      'estimacion de costos',
      'costos de materiales',
      'consultar base de precios',
      'precios de referencia',
      'base de costos',
      'calculadora materiales'
    ],
    desktop: {
      label: 'Estimación de costos',
      icon: <ClipboardList size={16} />
    },
    mobile: {
      label: '¿Necesitás una estimación rápida de costos?',
      summary: 'Te puedo guiar por la base de precios y ayudarte a armar un estimativo según rubros y regiones para arrancar tu presupuesto.'
    },
    resolveAction: () => actionHandlers['estimacion-costos'](navigate, userId)
  },
  {
    id: 'cotizar-materiales',
    keywords: [
      'cotizar materiales',
      'pedir cotizacion',
      'cotizacion materiales',
      'necesito proveedores',
      'solicitar precios materiales',
      'cotizar compra'
    ],
    desktop: {
      label: 'Pedir una cotización',
      icon: <Receipt size={16} />
    },
    mobile: {
      label: 'Pedir una cotización de materiales',
      summary: 'Completemos juntos qué necesitás y en qué plazos, así enviamos la solicitud a la red de proveedores para que te respondan.'
    },
    resolveAction: () => actionHandlers['cotizar-materiales'](navigate, userId)
  },
  {
    id: 'cotizaciones-materiales',
    keywords: [
      'ver cotizaciones',
      'explorar cotizaciones',
      'ofertas materiales',
      'responder cotizacion',
      'lista cotizaciones disponibles'
    ],
    desktop: {
      label: 'Explorar cotizaciones',
      icon: <ClipboardList size={16} />
    },
    mobile: {
      label: '¿Buscás cotizaciones ya publicadas por otros usuarios?',
      summary: 'Puedo mostrarte las solicitudes vigentes para que evalúes si querés responderlas o tomar como referencia sus precios.'
    },
    resolveAction: () => actionHandlers['cotizaciones-materiales'](navigate, userId)
  },
  {
    id: 'mis-cotizaciones-materiales',
    keywords: [
      'mis cotizaciones materiales',
      'cotizaciones enviadas',
      'administrar cotizaciones',
      'seguimiento cotizacion',
      'historial cotizaciones'
    ],
    desktop: {
      label: 'Mis cotizaciones de materiales',
      icon: <FolderOpen size={16} />
    },
    mobile: {
      label: '¿Querés revisar las cotizaciones que cargaste?',
      summary: 'Desde acá podés ver el estado de cada solicitud, responder mensajes de proveedores y actualizar cantidades o plazos.'
    },
    resolveAction: () => actionHandlers['mis-cotizaciones-materiales'](navigate, userData)
  },
  {
    id: 'encontrar-profesionales',
    keywords: [
      'encontrar profesionales',
      'buscar profesionales',
      'buscar empresas',
      'necesito perfiles',
      'ver profesionales',
      'buscar arquitecto',
      'consultar proveedor servicio',
      'buscar',
      'arquitecto',
      'contratistas',
      'contratista',
      'contratistas de obra',
      'contratistas de construccion',
    ],
    desktop: {
      label: 'Explorar perfiles',
      icon: <Users size={16} />
    },
    mobile: {
      label: '¿Buscás profesionales, empresas o contratistas para tu obra?',
      summary: 'Te muestro el directorio para que filtres por rubro, ubicación y calificaciones, y encuentres el perfil que necesitás.'
    },
    resolveAction: () => actionHandlers['encontrar-profesionales'](navigate, userId)
  },
  {
    id: 'postularme-ofertas',
    keywords: [
      'postularme',
      'postularme oferta',
      'ofertas trabajo',
      'busco trabajo construccion',
      'postularse obra',
      'aplicar trabajo',
      'ver ofertas trabajo',
      'trabajos disponibles'
    ],
    desktop: {
      label: 'Postularme a ofertas de trabajo',
      icon: <UserPlus size={16} />
    },
    mobile: {
      label: '¿Querés postularte a una oferta laboral?',
      summary: 'Te ayudo a repasar las ofertas disponibles para tu perfil y completar la postulación con tu documentación actualizada.'
    },
    resolveAction: () => actionHandlers['postularme-ofertas'](navigate, userId)
  },
  {
    id: 'publicar-oferta-trabajo',
    keywords: [
      'publicar vacante',
      'publicar oferta trabajo',
      'crear oferta trabajo',
      'buscar profesionales obra',
      'necesito contratar',
      'abrir oferta laboral',
      'publicar búsqueda'
    ],
    desktop: {
      label: 'Publicar vacante',
      icon: <FilePlus size={16} />
    },
    mobile: {
      label: '¿Necesitás publicar una vacante?',
      summary: 'Cargamos los detalles (rol, experiencia, modalidad) y publicamos tu vacante para que profesionales interesados se postulen.'
    },
    resolveAction: () => actionHandlers['publicar-oferta-trabajo'](navigate, userId)
  },
  {
    id: 'mis-postulaciones',
    keywords: [
      'mis postulaciones',
      'seguimiento postulaciones',
      'postulaciones enviadas',
      'estado postulacion',
      'entrevistas pendientes',
      'ver mis solicitudes'
    ],
    desktop: {
      label: 'Mis postulaciones',
      icon: <FileText size={16} />
    },
    mobile: {
      label: '¿Querés ver cómo van tus postulaciones?',
      summary: 'Acá podés revisar en qué estado está cada solicitud, responder mensajes de los reclutadores y actualizar tu disponibilidad.'
    },
    resolveAction: () => actionHandlers['mis-postulaciones'](navigate, userData)
  },
  {
    id: 'explorar-servicios',
    keywords: [
      'explorar servicios',
      'ver servicios',
      'buscar productos servicios',
      'marketplace servicios',
      'directorio servicios',
      'buscar servicios',
      'buscar productos',
      'buscar servicios y productos',
      'buscar servicios y productos disponibles',
      'buscar servicios y productos publicados',
      'buscar servicios y productos publicados disponibles'
    ],
    desktop: {
      label: 'Explorar posteos, servicios y productos',
      icon: <Store size={16} />
    },
    mobile: {
      label: '¿Querés ver qué posteos, servicios y productos hay publicados?',
      summary: 'Puedo mostrarte el feed de posteos, servicios y productos para que filtres por rubro o ubicación y contactes al proveedor.'
    },
    resolveAction: () => actionHandlers['explorar-servicios'](navigate, userId)
  },
  {
    id: 'ofrecer-servicios',
    keywords: [
      'publicar posteo',
      'crear posteo',
      'publicar post',
      'ofrecer servicios',
      'publicar servicio',
      'publicar producto',
      'agregar servicio',
      'vender servicio',
      'ofrecer productos',
      'vender productos',
      'ofrecer servicios',
      'vender servicios',
      'ofrecer servicios y productos',
      'vender servicios y productos',
      'ofrecer servicios y productos disponibles',
      'vender servicios y productos disponibles',
      'ofrecer servicios y productos publicados',
      'vender servicios y productos publicados',
      'ofrecer servicios y productos publicados disponibles',
      'vender servicios y productos publicados disponibles'
    ],
    desktop: {
      label: 'Publicar posteos o productos/servicios (se publican en Explorar)',
      icon: <Package size={16} />
    },
    mobile: {
      label: '¿Querés publicar un posteo o producto/servicio?',
      summary: 'Te guío para publicar un posteo o cargar tu oferta con fotos, precios y ubicación. Se publica en Explorar posteos, servicios y productos.'
    },
    resolveAction: () => actionHandlers['ofrecer-servicios'](navigate, userId, undefined, undefined, undefined, dispatch)
  },
  {
    id: 'mis-productos-servicios',
    keywords: [
      'mis productos',
      'mis servicios',
      'administrar marketplace',
      'editar servicio',
      'actualizar producto',
      'listar servicios publicados'
    ],
    desktop: {
      label: 'Mis productos/servicios',
      icon: <Store size={16} />
    },
    mobile: {
      label: '¿Querés editar o actualizar un servicio que ya publicaste?',
      summary: 'Desde acá podés revisar cada servicio, modificar precios, pausar publicaciones o responder consultas.'
    },
    resolveAction: () => actionHandlers['mis-productos-servicios'](navigate, userData)
  }
]

export interface DesktopSuggestion {
  id: string
  label: string
  icon: ReactNode
  keywords: string[]
  action: () => void
}

export interface MobileResponse {
  id: string
  label: string
  summary: string
  keywords: string[]
  action: () => void
}

export const getDesktopSuggestions = (
  navigate: NavigateFunction,
  userId?: string,
  userData?: any,
  dispatch?: any
): DesktopSuggestion[] =>
  buildDefinitions(navigate, userId, userData, dispatch).map(def => ({
    id: def.id,
    label: def.desktop.label,
    icon: def.desktop.icon,
    keywords: def.keywords,
    action: def.resolveAction
  }))

export const getPostFlowSuggestions = (onConfirmPost?: () => void): DesktopSuggestion[] => [
  {
    id: 'guardar-post',
    label: 'Publicar',
    icon: <Package size={16} />,
    keywords: [],
    action: onConfirmPost ?? (() => {})
  }
]

export const getMobileResponses = (
  navigate: NavigateFunction,
  userId?: string,
  userData?: any,
  dispatch?: any
): MobileResponse[] =>
  buildDefinitions(navigate, userId, userData, dispatch).map(def => ({
    id: def.id,
    label: def.mobile.label,
    summary: def.mobile.summary,
    keywords: def.keywords,
    action: def.resolveAction
  }))

